import React from 'react'
import {
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'

import {
  HOVER_DIMMED_OPACITY,
  HOVER_FOCUSED_OPACITY,
  HOVER_TRANSITION_MS,
  HOVER_TRANSLUCENT_BASE_OPACITY,
  HOVER_TRANSLUCENT_DIMMED_OPACITY,
} from 'features/charts-v2/chart-hover-constants'
import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import {
  RATING_THRESHOLDS,
  RATING_ZONE_COLORS,
  SENTIMENT_THRESHOLDS,
  SENTIMENT_ZONE_COLORS,
} from 'features/charts-v2/constants'
import { useBreakdownColorMap } from 'features/charts-v2/context/breakdown-color-map-context'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import {
  computePaddedScatterDomain,
  formatScatterAxisTick,
  generateScatterTicks,
} from 'features/charts-v2/utils/axis-calculations'
import { getMetricAxisDomain } from 'features/charts-v2/utils/axis-tick-utils'
import { buildBreakdownColorMap } from 'features/charts-v2/utils/breakdown-color-map'
import { getMetricLabel } from 'features/charts-v2/utils/metric-format-utils'
import {
  computeVisibleLabels,
  getLabelThreshold,
  processLabel,
} from 'features/charts-v2/utils/process-label'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;

  /* Dim non-hovered scatter points when any point is hovered */
  &:has(circle[data-scatter-name]:hover) circle[data-scatter-name] {
    fill-opacity: ${HOVER_TRANSLUCENT_DIMMED_OPACITY};
    stroke-opacity: ${HOVER_DIMMED_OPACITY};
    transition:
      fill-opacity ${HOVER_TRANSITION_MS}ms ease,
      stroke-opacity ${HOVER_TRANSITION_MS}ms ease;
  }

  /* Keep the hovered point at its base translucent appearance */
  &:has(circle[data-scatter-name]:hover) circle[data-scatter-name]:hover {
    fill-opacity: ${HOVER_TRANSLUCENT_BASE_OPACITY} !important;
    stroke-opacity: ${HOVER_FOCUSED_OPACITY} !important;
  }
`

/**
 * Zone object for ReferenceArea
 */
interface ZoneObject {
  x1?: number
  x2?: number
  y1?: number
  y2?: number
  fill: string
}

/**
 * Compute background zones for sentiment or rating axes
 */
function computeZones(
  axis: 'x' | 'y',
  domain: [number, number] | undefined,
  negativeThreshold: number,
  positiveThreshold: number,
  absoluteMin: number,
  absoluteMax: number,
  colors:
    | { negative: string; neutral: string; positive: string }
    | { negative: string; positive: string },
): ZoneObject[] {
  const zones: ZoneObject[] = []
  const domainMin = domain?.[0] !== undefined ? domain[0] : absoluteMin
  const domainMax = domain?.[1] !== undefined ? domain[1] : absoluteMax

  const isXAxis = axis === 'x'

  if (domainMin < negativeThreshold) {
    const bound1 = Math.max(domainMin, absoluteMin)
    const bound2 = Math.min(domainMax, negativeThreshold)
    if (bound2 > bound1) {
      zones.push({
        ...(isXAxis ? { x1: bound1, x2: bound2 } : { y1: bound1, y2: bound2 }),
        fill: colors.negative,
      })
    }
  }

  if ('neutral' in colors) {
    if (domainMin < positiveThreshold && domainMax > negativeThreshold) {
      const bound1 = Math.max(domainMin, negativeThreshold)
      const bound2 = Math.min(domainMax, positiveThreshold)
      if (bound2 > bound1) {
        zones.push({
          ...(isXAxis
            ? { x1: bound1, x2: bound2 }
            : { y1: bound1, y2: bound2 }),
          fill: colors.neutral,
        })
      }
    }
  }

  if (domainMax > positiveThreshold) {
    const bound1 = Math.max(domainMin, positiveThreshold)
    const bound2 = Math.min(domainMax, absoluteMax)
    if (bound2 > bound1) {
      zones.push({
        ...(isXAxis ? { x1: bound1, x2: bound2 } : { y1: bound1, y2: bound2 }),
        fill: colors.positive,
      })
    }
  }

  return zones
}

// Scatter data point type
interface ScatterDataPoint {
  name: string
  x: number
  y: number
  z?: number
}

interface ScatterShapeProps {
  cx: number
  cy: number
  payload: ScatterDataPoint
}

interface ScatterChartViewProps {
  data: ChartProps['data']
  /** Unfiltered dataset for stable label→color mapping; see ChartProps.colorReferenceData. */
  colorReferenceData?: ChartProps['colorReferenceData']
  transformedData: TransformedChartData
  selectedMetrics: MetricType[]
  selectedBreakdowns?: ChartProps['selectedBreakdowns']
  onDataPointHover?: (name: string | null) => void
  onSegmentClick?: ChartProps['onSegmentClick']
  fontSizePx: number
  showXAxisLabel: boolean
  showYAxisLabel: boolean
  showDatapointLabels: boolean
  showSentimentZones: boolean
  showRatingZones: boolean
  xAxisDomain?: [number, number]
  yAxisDomain?: [number, number]
  disableAnimation: boolean
}

// eslint-disable-next-line react-refresh/only-export-components -- internal helper component
const ScatterChartViewInner: React.FC<ScatterChartViewProps> = ({
  data,
  colorReferenceData,
  transformedData,
  selectedMetrics,
  selectedBreakdowns,
  onDataPointHover,
  onSegmentClick,
  fontSizePx,
  showXAxisLabel,
  showYAxisLabel,
  showDatapointLabels,
  showSentimentZones,
  showRatingZones,
  xAxisDomain,
  yAxisDomain,
  disableAnimation,
}) => {
  const contextColorMap = useBreakdownColorMap()
  const colorMap =
    contextColorMap.size > 0
      ? contextColorMap
      : buildBreakdownColorMap(
          selectedBreakdowns?.[0] ?? null,
          colorReferenceData ?? data,
        )
  const defaultColor = chartConfig.getColor('primary', 0)

  const scatterData = transformedData.chartData as unknown as ScatterDataPoint[]

  // Dynamic labels based on selectedMetrics order
  const xAxisLabel = selectedMetrics[0]!
    ? getMetricLabel(selectedMetrics[0]!)
    : 'X-Axis'
  const yAxisLabel = selectedMetrics[1]!
    ? getMetricLabel(selectedMetrics[1]!)
    : 'Y-Axis'

  // Check which axis has sentiment
  const xAxisIsSentiment = selectedMetrics[0] === METRICS.SENTIMENT.id
  const yAxisIsSentiment = selectedMetrics[1] === METRICS.SENTIMENT.id

  // Check which axis has rating
  const xAxisIsRating = selectedMetrics[0] === METRICS.RATING.id
  const yAxisIsRating = selectedMetrics[1] === METRICS.RATING.id

  // Compute effective domains based on metric type and data
  const computeDomainFromData = (
    pts: ScatterDataPoint[],
    accessor: (d: ScatterDataPoint) => number,
    metricType?: MetricType,
  ): [number, number] | undefined => {
    if (pts.length === 0) return undefined
    const values = pts.map(accessor).filter((v) => Number.isFinite(v))
    if (values.length === 0) return undefined
    const min = Math.min(...values)
    const max = Math.max(...values)
    return computePaddedScatterDomain([min, max], metricType)
  }

  const effectiveXAxisDomain: [number, number] = xAxisDomain ??
    computeDomainFromData(scatterData, (d) => d.x, selectedMetrics[0]) ?? [0, 1]

  const effectiveYAxisDomain: [number, number] = yAxisDomain ??
    computeDomainFromData(scatterData, (d) => d.y, selectedMetrics[1]) ?? [0, 1]

  const xTicks = generateScatterTicks(
    effectiveXAxisDomain[0],
    effectiveXAxisDomain[1],
    selectedMetrics[0],
  )
  const yTicks = generateScatterTicks(
    effectiveYAxisDomain[0],
    effectiveYAxisDomain[1],
    selectedMetrics[1],
  )

  const extendedYDomain: [number, number] = [
    Math.min(effectiveYAxisDomain[0], yTicks[0] ?? effectiveYAxisDomain[0]),
    Math.max(
      effectiveYAxisDomain[1],
      yTicks[yTicks.length - 1] ?? effectiveYAxisDomain[1],
    ),
  ]

  const extendedXDomain: [number, number] = [
    Math.min(effectiveXAxisDomain[0], xTicks[0] ?? effectiveXAxisDomain[0]),
    Math.max(
      effectiveXAxisDomain[1],
      xTicks[xTicks.length - 1] ?? effectiveXAxisDomain[1],
    ),
  ]

  // Clamp extended domains to metric bounds
  const yMetricBounds = getMetricAxisDomain(selectedMetrics[1])
  if (yMetricBounds) {
    extendedYDomain[0] = Math.max(extendedYDomain[0], yMetricBounds[0])
    extendedYDomain[1] = Math.min(extendedYDomain[1], yMetricBounds[1])
  }

  const xMetricBounds = getMetricAxisDomain(selectedMetrics[0])
  if (xMetricBounds) {
    extendedXDomain[0] = Math.max(extendedXDomain[0], xMetricBounds[0])
    extendedXDomain[1] = Math.min(extendedXDomain[1], xMetricBounds[1])
  }

  // Calculate average values for reference lines
  const avgX =
    scatterData.length > 0
      ? scatterData.reduce((sum, item) => sum + item.x, 0) / scatterData.length
      : 0
  const avgY =
    scatterData.length > 0
      ? scatterData.reduce((sum, item) => sum + item.y, 0) / scatterData.length
      : 0

  // Calculate background zones
  const backgroundZones: ZoneObject[] = []

  if (showSentimentZones) {
    if (xAxisIsSentiment) {
      backgroundZones.push(
        ...computeZones(
          'x',
          extendedXDomain,
          SENTIMENT_THRESHOLDS.NEGATIVE,
          SENTIMENT_THRESHOLDS.POSITIVE,
          -1,
          1,
          SENTIMENT_ZONE_COLORS,
        ),
      )
    } else if (yAxisIsSentiment) {
      backgroundZones.push(
        ...computeZones(
          'y',
          extendedYDomain,
          SENTIMENT_THRESHOLDS.NEGATIVE,
          SENTIMENT_THRESHOLDS.POSITIVE,
          -1,
          1,
          SENTIMENT_ZONE_COLORS,
        ),
      )
    }
  }

  if (showRatingZones) {
    if (xAxisIsRating) {
      backgroundZones.push(
        ...computeZones(
          'x',
          extendedXDomain,
          RATING_THRESHOLDS.NEGATIVE,
          RATING_THRESHOLDS.POSITIVE,
          1,
          5,
          RATING_ZONE_COLORS,
        ),
      )
    } else if (yAxisIsRating) {
      backgroundZones.push(
        ...computeZones(
          'y',
          extendedYDomain,
          RATING_THRESHOLDS.NEGATIVE,
          RATING_THRESHOLDS.POSITIVE,
          1,
          5,
          RATING_ZONE_COLORS,
        ),
      )
    }
  }

  // Get short metric names for reference line labels
  const getShortMetricLabel = (metricType: MetricType): string => {
    switch (metricType) {
      case METRICS.SENTIMENT.id:
        return 'Sentiment'
      case METRICS.RATING.id:
        return 'Rating'
      case METRICS.SENTENCE_COUNT.id:
        return 'Sentences'
      case METRICS.RECORD_COUNT.id:
        return 'Records'
      case METRICS.NET_PROMOTER_SCORE.id:
        return 'NPS'
      default:
        return metricType
    }
  }

  const xMetricName = selectedMetrics[0]!
    ? getShortMetricLabel(selectedMetrics[0]!)
    : 'X'
  const yMetricName = selectedMetrics[1]!
    ? getShortMetricLabel(selectedMetrics[1]!)
    : 'Y'

  // Pre-compute visible labels to avoid mutation during render
  const ESTIMATED_CHART_WIDTH = 800
  const ESTIMATED_CHART_HEIGHT = 500
  const xRange = extendedXDomain[1] - extendedXDomain[0]
  const yRange = extendedYDomain[1] - extendedYDomain[0]

  const visibleLabels = computeVisibleLabels(
    scatterData,
    fontSizePx,
    (d) =>
      xRange > 0
        ? ((d.x - extendedXDomain[0]) / xRange) * ESTIMATED_CHART_WIDTH
        : 0,
    (d) =>
      yRange > 0
        ? ESTIMATED_CHART_HEIGHT -
          ((d.y - extendedYDomain[0]) / yRange) * ESTIMATED_CHART_HEIGHT
        : 0,
    () => 6,
    6,
  )

  return (
    <ChartContainer>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart
          data={scatterData}
          margin={{
            top: 20,
            right: 30,
            bottom: 60,
            left: 60,
          }}
          onMouseLeave={() => onDataPointHover?.(null)}
        >
          {/* Sentiment and rating background zones */}
          {backgroundZones.map((zone, index) => (
            <ReferenceArea
              key={`zone-${index}`}
              x1={zone.x1}
              x2={zone.x2}
              y1={zone.y1}
              y2={zone.y2}
              fill={zone.fill}
              fillOpacity={1}
            />
          ))}

          <CartesianGrid strokeDasharray="3 3" />

          {/* Average reference lines */}
          {scatterData && scatterData.length > 0 && (
            <>
              <ReferenceLine
                x={avgX}
                stroke="#475569"
                strokeWidth={2}
                label={{
                  value: `Average ${xMetricName}: ${formatScatterAxisTick(avgX, selectedMetrics[0])}`,
                  position: 'top',
                  fill: '#1e293b',
                  fontSize: fontSizePx,
                  fontWeight: 500,
                }}
              />
              <ReferenceLine
                y={avgY}
                stroke="#475569"
                strokeWidth={2}
                label={{
                  value: `Average ${yMetricName}: ${formatScatterAxisTick(avgY, selectedMetrics[1])}`,
                  position: 'insideTopLeft',
                  fill: '#1e293b',
                  fontSize: fontSizePx,
                  fontWeight: 500,
                }}
              />
            </>
          )}

          <XAxis
            type="number"
            dataKey="x"
            name={xAxisLabel}
            domain={extendedXDomain}
            ticks={xTicks}
            tickFormatter={(value) =>
              formatScatterAxisTick(value, selectedMetrics[0])
            }
            tick={{ fontSize: fontSizePx }}
            allowDataOverflow={true}
            label={
              showXAxisLabel
                ? {
                    value: xAxisLabel,
                    position: 'insideBottom',
                    offset: -5,
                    style: {
                      textAnchor: 'middle',
                      fill: '#666',
                      fontSize: fontSizePx,
                    },
                  }
                : undefined
            }
          />
          <YAxis
            type="number"
            dataKey="y"
            name={yAxisLabel}
            domain={extendedYDomain}
            ticks={yTicks}
            tickFormatter={(value) =>
              formatScatterAxisTick(value, selectedMetrics[1])
            }
            tick={{ fontSize: fontSizePx }}
            allowDataOverflow={true}
            label={
              showYAxisLabel
                ? {
                    value: yAxisLabel,
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      textAnchor: 'middle',
                      fill: '#666',
                      fontSize: fontSizePx,
                    },
                  }
                : undefined
            }
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload[0]) {
                const pt = payload[0].payload
                return (
                  <ChartDatapointHoverAnnotation
                    title={pt.name}
                    rows={[
                      {
                        label: xAxisLabel,
                        value:
                          pt.x != null
                            ? formatScatterAxisTick(pt.x, selectedMetrics[0])
                            : 'N/A',
                        percentage: null,
                        color: null,
                      },
                      {
                        label: yAxisLabel,
                        value:
                          pt.y != null
                            ? formatScatterAxisTick(pt.y, selectedMetrics[1])
                            : 'N/A',
                        percentage: null,
                        color: null,
                      },
                    ]}
                    footer={null}
                    focusedField={null}
                  />
                )
              }
              return null
            }}
          />
          <Scatter
            dataKey="z"
            name="Themes"
            isAnimationActive={!showDatapointLabels && !disableAnimation}
            onClick={(
              pt: ScatterDataPoint,
              _index: number,
              event: React.MouseEvent,
            ) => {
              if (onSegmentClick && pt) {
                onSegmentClick(
                  {
                    primaryBreakdown: pt.name,
                    value: pt.z || pt.y || pt.x || 0,
                    payload: pt as unknown as Record<
                      string,
                      string | number | boolean | null
                    >,
                  },
                  event,
                )
              }
            }}
            shape={
              ((scatterProps: ScatterShapeProps) => {
                const radius = 6

                const shouldShowLabel =
                  showDatapointLabels &&
                  visibleLabels.has(scatterProps.payload?.name)
                const wrappedLines = shouldShowLabel
                  ? processLabel(
                      getLabelThreshold(fontSizePx),
                      scatterProps.payload.name,
                    ).split('\n')
                  : []

                const pointColor =
                  colorMap.get(scatterProps.payload.name) ?? defaultColor

                return (
                  <g>
                    <circle
                      cx={scatterProps.cx}
                      cy={scatterProps.cy}
                      r={radius}
                      fill={pointColor}
                      stroke={pointColor}
                      strokeWidth={2}
                      fillOpacity={HOVER_TRANSLUCENT_BASE_OPACITY}
                      strokeOpacity={0.9}
                      data-scatter-name={scatterProps.payload.name}
                      onMouseEnter={() =>
                        onDataPointHover?.(scatterProps.payload.name)
                      }
                      onMouseLeave={() => onDataPointHover?.(null)}
                      style={{ cursor: 'pointer' }}
                    />
                    {shouldShowLabel && (
                      <text
                        x={scatterProps.cx}
                        y={scatterProps.cy - 12}
                        fill="#333"
                        fontSize={fontSizePx}
                        textAnchor="middle"
                        style={{
                          fontFamily:
                            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          pointerEvents: 'none',
                        }}
                      >
                        {wrappedLines.map((line, idx) => (
                          <tspan
                            key={idx}
                            x={scatterProps.cx}
                            dy={idx === 0 ? 0 : fontSizePx * 0.8}
                          >
                            {line}
                          </tspan>
                        ))}
                      </text>
                    )}
                  </g>
                )
              }) as never
            }
          />
        </ScatterChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

const ScatterChartView = React.memo(ScatterChartViewInner)

export class ScatterChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.SCATTER
  public readonly requiredMetrics: MetricType[] = [
    METRICS.RATING.id,
    METRICS.SENTIMENT.id,
  ]
  public readonly optionalMetrics: MetricType[] = []

  public transform(props: ChartProps): TransformedChartData {
    const breakdowns = props.selectedBreakdowns || []

    if (breakdowns.length === 0 && props.data && props.data.data.length > 0) {
      const totalRecords = props.data.data.length
      let sumSentiment = 0
      let sumRating = 0

      props.data.data.forEach((item) => {
        if (item.values[0] != null) sumSentiment += item.values[0]
        if (item.values[1] != null) sumRating += item.values[1]
      })

      const avgSentiment = totalRecords > 0 ? sumSentiment / totalRecords : 0
      const avgRating = totalRecords > 0 ? sumRating / totalRecords : 0

      return {
        chartData: [
          {
            name: 'Total',
            x: avgSentiment,
            y: avgRating,
            z: 1,
          },
        ],
      }
    }

    return dataTransformationService.transformForScatterPlot(
      props.data!,
      props.selectedMetrics,
      props.timeBucket,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    if (
      !props.transformedData.chartData ||
      props.transformedData.chartData.length === 0
    ) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    return (
      <ScatterChartView
        data={props.data}
        colorReferenceData={props.colorReferenceData}
        transformedData={props.transformedData}
        selectedMetrics={props.selectedMetrics}
        selectedBreakdowns={props.selectedBreakdowns}
        onDataPointHover={props.onDataPointHover}
        onSegmentClick={props.onSegmentClick}
        fontSizePx={props.fontSizePx ?? 13}
        showXAxisLabel={props.showXAxisLabel ?? true}
        showYAxisLabel={props.showYAxisLabel ?? true}
        showDatapointLabels={props.showDatapointLabels ?? true}
        showSentimentZones={props.showSentimentZones ?? true}
        showRatingZones={props.showRatingZones ?? true}
        xAxisDomain={props.xAxisDomain}
        yAxisDomain={props.yAxisDomain}
        disableAnimation={props.disableAnimation ?? false}
      />
    )
  }

  public getTitle(props?: ChartProps): string {
    if (!props || !props.selectedMetrics || props.selectedMetrics.length < 2) {
      return 'Scatter Chart'
    }

    const yLabel = getMetricLabel(props.selectedMetrics[1]!)
    const xLabel = getMetricLabel(props.selectedMetrics[0]!)
    return `${yLabel} vs. ${xLabel} (Scatter)`
  }
}
